// frontend/src/pages/Login.jsx
import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { API_BASE_URL } from "../config/api";
import Swal from "sweetalert2";
import { Eye, EyeOff, Mail, Lock, LogIn } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [generalError, setGeneralError] = useState("");
  const [credentialError, setCredentialError] = useState("");
  const navigate = useNavigate();

  const credentialTimeoutRef = useRef(null);
  const generalTimeoutRef = useRef(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      if (credentialTimeoutRef.current) {
        clearTimeout(credentialTimeoutRef.current);
      }
      if (generalTimeoutRef.current) {
        clearTimeout(generalTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!credentialError || !isMountedRef.current) return;

    if (credentialTimeoutRef.current) {
      clearTimeout(credentialTimeoutRef.current);
    }

    credentialTimeoutRef.current = setTimeout(() => {
      if (isMountedRef.current) {
        setCredentialError("");
      }
    }, 15000);

    return () => {
      if (credentialTimeoutRef.current) {
        clearTimeout(credentialTimeoutRef.current);
      }
    };
  }, [credentialError]);

  useEffect(() => {
    if (!generalError || !isMountedRef.current) return;

    if (generalTimeoutRef.current) {
      clearTimeout(generalTimeoutRef.current);
    }

    generalTimeoutRef.current = setTimeout(() => {
      if (isMountedRef.current) {
        setGeneralError("");
      }
    }, 60000);

    return () => {
      if (generalTimeoutRef.current) {
        clearTimeout(generalTimeoutRef.current);
      }
    };
  }, [generalError]);

  const handleLogin = async (e) => {
    e.preventDefault();

    if (credentialTimeoutRef.current) {
      clearTimeout(credentialTimeoutRef.current);
      credentialTimeoutRef.current = null;
    }
    if (generalTimeoutRef.current) {
      clearTimeout(generalTimeoutRef.current);
      generalTimeoutRef.current = null;
    }

    setCredentialError("");
    setGeneralError("");
    setLoading(true);

    if (!email || !password) {
      setCredentialError("⚠️ Preencha todos os campos");
      setLoading(false);
      return;
    }

    Swal.fire({
      title: "Entrando...",
      text: "Por favor, aguarde",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      },
      customClass: {
        popup: "text-sm rounded-xl",
      },
    });

    try {
      const result = await login(email.trim(), password.trim());

      Swal.close();

      if (result.success) {
        const userData = JSON.parse(localStorage.getItem("user"));

        Swal.fire({
          icon: "success",
          title: "Bem-vindo!",
          text: `Olá, ${userData?.apelido || userData?.name || "Usuário"}!`,
          timer: 2000,
          showConfirmButton: false,
          position: "top-end",
          toast: true,
          customClass: {
            popup: "text-sm rounded-xl",
          },
        });

        if (userData?.role === "admin") {
          navigate("/admin");
        } else {
          navigate("/");
        }
      } else {
        const errorMsg = result.message || "Erro ao fazer login";

        if (
          errorMsg.includes("Credenciais") ||
          errorMsg.includes("inválidas")
        ) {
          setCredentialError(` ${errorMsg}`);
        } else if (
          errorMsg.includes("validação") ||
          errorMsg.includes("pendente")
        ) {
          setGeneralError(errorMsg);
        } else {
          setGeneralError(` ${errorMsg}`);
        }

        Swal.fire({
          icon: "error",
          title: "Falha no login",
          text: errorMsg,
          confirmButtonColor: "#449779",
          confirmButtonText: "OK",
          customClass: {
            popup: "text-sm rounded-xl",
          },
        });
      }
    } catch (err) {
      Swal.close();

      if (!isMountedRef.current) return;

      console.error("❌ Error en login:", err);

      let errorMessage = "⚠️ Erro no servidor";

      if (err.response?.status === 401) {
        errorMessage = "Email ou senha incorretos";
        setCredentialError(`⚠️ ${errorMessage}`);
      } else if (err.response?.status === 403) {
        errorMessage =
          "Estamos validando sua conta. Assim que estiver tudo certo, avisamos você.";
        setGeneralError(errorMessage);
      } else if (err.response?.status === 422) {
        errorMessage = "Dados inválidos. Verifique os campos.";
        setGeneralError(`⚠️ ${errorMessage}`);
      } else if (err.code === "ERR_NETWORK") {
        errorMessage = "Sem conexão com o servidor";
        setGeneralError(`⚠️ ${errorMessage}`);
      } else {
        setGeneralError(`⚠️ ${errorMessage}`);
      }

      Swal.fire({
        icon: "error",
        title: "Erro",
        text: errorMessage,
        confirmButtonColor: "#449779",
        confirmButtonText: "OK",
        customClass: {
          popup: "text-sm rounded-xl",
        },
      });
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  const handleCloseCredentialError = () => {
    setCredentialError("");
    if (credentialTimeoutRef.current) {
      clearTimeout(credentialTimeoutRef.current);
      credentialTimeoutRef.current = null;
    }
  };

  const handleCloseGeneralError = () => {
    setGeneralError("");
    if (generalTimeoutRef.current) {
      clearTimeout(generalTimeoutRef.current);
      generalTimeoutRef.current = null;
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-8">
      {/* Logo y título */}
      <div className="text-center mb-8">
        {/* 🔥 REEMPLAZADO: "BV" por imagen brecho.png */}
        <div className="flex justify-center mb-4">
          <img
            src={`${process.env.PUBLIC_URL}/brecho.png`}
            alt="Brechó Vizinho"
            className="w-16 h-16 object-contain"
          />
        </div>
        <h1 className="text-2xl font-bold text-gray-800">Entre na sua conta</h1>
        <p className="text-sm text-gray-500 mt-1">
          Negocie com segurança dentro da sua comunidade!
        </p>
      </div>

      {/* Error General */}
      {generalError && (
        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 text-blue-700 rounded-xl">
          <div className="flex justify-between items-center">
            <div className="flex-1 text-sm">{generalError}</div>
            <button
              onClick={handleCloseGeneralError}
              className="text-blue-500 hover:text-blue-700 ml-2"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      <form onSubmit={handleLogin} className="space-y-5">
        {/* Campo Email */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            E-mail
          </label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="email"
              placeholder="seu@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#449779] focus:border-transparent"
              required
              disabled={loading}
            />
          </div>
        </div>

        {/* Campo Senha */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Senha
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type={showPassword ? "text" : "password"}
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#449779] focus:border-transparent"
              required
              disabled={loading}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2"
            >
              {showPassword ? (
                <EyeOff className="w-5 h-5 text-gray-400" />
              ) : (
                <Eye className="w-5 h-5 text-gray-400" />
              )}
            </button>
          </div>
        </div>

        {/* Link Esqueci a senha */}
        <div className="text-right">
          <a
            href="/esqueci-senha"
            className="text-sm text-[#449779] hover:underline"
          >
            Esqueci minha senha
          </a>
        </div>

        {/* Error de credencial */}
        {credentialError && (
          <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl">
            <div className="flex justify-between items-center">
              <div className="flex-1">
                <span className="font-medium text-sm">{credentialError}</span>
                <p className="text-xs text-red-600 mt-1">
                  Verifique se digitou corretamente ou recupere sua senha.
                </p>
              </div>
              <button
                onClick={handleCloseCredentialError}
                className="text-red-500 hover:text-red-700 ml-2"
              >
                ✕
              </button>
            </div>
          </div>
        )}

        {/* Botón Acessar */}
        <button
          type="submit"
          disabled={loading}
          className="w-full bg-[#449779] text-white py-3 rounded-xl font-semibold hover:bg-[#2d5f4d] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {loading ? (
            "Entrando..."
          ) : (
            <>
              <LogIn className="w-5 h-5" />
              Acessar
            </>
          )}
        </button>
      </form>

      {/* Link para cadastro */}
      <div className="mt-8 text-center">
        <p className="text-sm text-gray-600">
          Não tem uma conta?{" "}
          <Link
            to="/registro"
            className="text-[#449779] font-semibold hover:underline"
          >
            Cadastre-se
          </Link>
        </p>
      </div>

      {/* Texto legal */}
      <div className="mt-6 text-center text-[11px] text-gray-400 leading-relaxed">
        Ao continuar, você concorda com os{" "}
        <a href="/termos" className="text-[#449779] hover:underline">
          Termos de Uso
        </a>{" "}
        e a{" "}
        <a href="/privacidade" className="text-[#449779] hover:underline">
          Política de Privacidade
        </a>{" "}
        do Brechó Vizinho.
      </div>
    </div>
  );
}
